import React, { useState, useEffect } from 'react'
import { X, Plus, Edit, Trash2, Filter, ArrowLeft } from 'lucide-react'
import { notificationsService } from '../services/notificationsService'
import { propertiesService } from '../services/propertiesService'
import { getNotificationRegionOptions, getRegionLabel } from '../helpers/propertyTypeHelpers'
import { usePropertyMetadata } from '../contexts/PropertyMetadataContext'

const NotificationsModal = ({ isOpen, onClose, onApplyAlert }) => {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [currentView, setCurrentView] = useState('list') // 'list' or 'edit'
  const [editingAlert, setEditingAlert] = useState(null)

  // Fetch alerts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAlerts()
    }
  }, [isOpen])

  const fetchAlerts = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await notificationsService.getAlerts()
      setAlerts(data)
    } catch (err) {
      setError('Nepodařilo se načíst alerty')
      console.error('Error fetching alerts:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAlert = async (alertId) => {
    try {
      await notificationsService.deleteAlert(alertId)
      setAlerts(alerts.filter((alert) => alert.id !== alertId))
      setError(null)
    } catch (err) {
      setError('Nepodařilo se smazat alert')
      console.error('Error deleting alert:', err)
    }
  }

  const handleToggleAlert = async (alertId) => {
    try {
      const updatedAlert = alerts.find((alert) => alert.id === alertId)
      const newActiveState = !updatedAlert.active

      await notificationsService.toggleAlert(alertId, newActiveState)
      setAlerts(alerts.map((alert) => (alert.id === alertId ? { ...alert, active: newActiveState } : alert)))
      setError(null)
    } catch (err) {
      setError('Nepodařilo se změnit stav alertu')
      console.error('Error toggling alert:', err)
    }
  }

  const handleApplyAlert = (alert) => {
    onApplyAlert(alert)
    onClose()
  }

  const getPropertyTypeLabel = (type) => {
    const labels = {
      byt: 'Byt',
      dum: 'Dům',
      pozemek: 'Pozemek',
      chata: 'Chata',
    }
    return labels[type] || type
  }

  const getOfferTypeLabel = (offerType) => {
    return offerType === 'prodej' ? 'Prodej' : 'Pronájem'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          {currentView === 'list' ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Notifikace</h2>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md">
                <X size={20} />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setCurrentView('list')
                    setEditingAlert(null)
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md"
                >
                  <ArrowLeft size={20} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">{editingAlert ? 'Upravit alert' : 'Vytvořit alert'}</h2>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md">
                <X size={20} />
              </button>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {currentView === 'list' ? (
            <>
              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Create New Alert Button */}
              <div className="mb-6">
                <button
                  onClick={() => {
                    setEditingAlert(null)
                    setCurrentView('edit')
                    setError(null)
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} />
                  Vytvořit nový alert
                </button>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-8">
                  <div className="text-gray-500">Načítání alertů...</div>
                </div>
              )}

              {/* Alerts List */}
              {!loading && (
                <div className="space-y-4">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-gray-900">{alert.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs ${alert.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {alert.active ? 'Aktivní' : 'Neaktivní'}
                            </span>
                          </div>

                          <div className="text-sm text-gray-600 space-y-1">
                            <div>
                              {getPropertyTypeLabel(alert.type)} • {getRegionLabel(alert.region)} • {getOfferTypeLabel(alert.offerType)}
                            </div>
                            <div>
                              {alert.conditions.priceMin && `Od ${alert.conditions.priceMin.toLocaleString()} Kč/m²`}
                              {alert.conditions.priceMax && ` Do ${alert.conditions.priceMax.toLocaleString()} Kč/m²`}
                            </div>
                            {alert.layouts && alert.layouts.length > 0 && <div>Dispozice: {alert.layouts.join(', ')}</div>}
                            {alert.conditions.stavba && alert.conditions.stavba.length > 0 && (
                              <div>Stavba: {alert.conditions.stavba.map((s) => s.replace('stavba-', '')).join(', ')}</div>
                            )}
                            {/* <div className="text-blue-600 font-medium">{alert.matchCount} odpovídajících nemovitostí</div> */}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <button onClick={() => handleApplyAlert(alert)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" title="Aplikovat alert">
                            <Filter size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingAlert(alert)
                              setCurrentView('edit')
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-md"
                            title="Upravit"
                          >
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDeleteAlert(alert.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Smazat">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {alerts.length === 0 && <div className="text-center py-8 text-gray-500">Zatím nemáte žádné alerty. Vytvořte první alert pro sledování nemovitostí.</div>}
            </>
          ) : (
            <CreateAlertForm
              alert={editingAlert}
              onSave={async (alertData) => {
                try {
                  setError(null)

                  if (editingAlert) {
                    // Update existing alert
                    const updatedAlert = await notificationsService.updateAlert(editingAlert._id, alertData)
                    setAlerts(alerts.map((a) => (a._id === editingAlert._id ? updatedAlert : a)))
                  } else {
                    // Create new alert
                    const newAlert = await notificationsService.createAlert(alertData)
                    setAlerts([...alerts, newAlert])
                  }
                } catch (err) {
                  setError(editingAlert ? 'Nepodařilo se upravit alert' : 'Nepodařilo se vytvořit alert')
                  console.error('Error saving alert:', err)
                } finally {
                  // Always go back to list view
                  setCurrentView('list')
                  setEditingAlert(null)
                }
              }}
              onCancel={() => {
                setCurrentView('list')
                setEditingAlert(null)
                setError(null)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

const CreateAlertForm = ({ alert, onSave, onCancel }) => {
  const { filterConditions } = usePropertyMetadata()
  const [formData, setFormData] = useState({
    name: '',
    slackChannelId: '',
    type: 'byt',
    region: 'brno',
    offerType: 'prodej',
    districts: [],
    layouts: [],
    linkToJestrab: false,
    valuationEnabled: false,
    conditions: {
      priceMin: '',
      priceMinType: 'number', // 'number' or 'special'
      priceMax: '',
      priceMaxType: 'number', // 'number' or 'special'
      stavba: [],
    },
  })

  const [availableDistricts, setAvailableDistricts] = useState([])
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const regionOptions = getNotificationRegionOptions(formData.type)

  // Fetch districts when region changes
  const fetchDistricts = async (region) => {
    setLoadingDistricts(true)
    try {
      const data = await propertiesService.fetchDistricts(region)
      setAvailableDistricts(data?.districts || [])
    } catch (error) {
      console.error('Error fetching districts:', error)
      setAvailableDistricts([])
    } finally {
      setLoadingDistricts(false)
    }
  }

  // Fetch districts when region changes
  useEffect(() => {
    fetchDistricts(formData.region)
  }, [formData.region])

  useEffect(() => {
    const nextRegionOptions = getNotificationRegionOptions(formData.type)

    if (nextRegionOptions.some((region) => region.value === formData.region)) {
      return
    }

    setFormData((prev) => ({
      ...prev,
      region: nextRegionOptions[0]?.value || '',
      districts: [],
    }))
  }, [formData.type, formData.region])

  // Reset form data when alert changes
  useEffect(() => {
    if (alert) {
      setFormData({
        id: alert._id, // Use _id from MongoDB
        name: alert.name,
        slackChannelId: alert.slackChannelId || '',
        type: alert.type,
        region: alert.region,
        offerType: alert.offerType,
        districts: alert.districts,
        layouts: alert.layouts,
        linkToJestrab: alert.linkToJestrab || false,
        valuationEnabled: alert.valuationEnabled || false,
        conditions: {
          priceMin: alert.conditions.priceMin,
          priceMinType: alert.conditions.priceMinType || 'number',
          priceMax: alert.conditions.priceMax,
          priceMaxType: alert.conditions.priceMaxType || 'number',
          stavba: alert.conditions.stavba,
        },
      })
    }
  }, [alert])

  const [isSaving, setIsSaving] = useState(false)

  const handleDistrictChange = (districtName) => {
    const isSelected = formData.districts.some((d) => d.toLowerCase() === districtName.toLowerCase())
    setFormData({
      ...formData,
      districts: isSelected ? formData.districts.filter((d) => d.toLowerCase() !== districtName.toLowerCase()) : [...formData.districts, districtName],
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await onSave(formData)
    } catch (error) {
      console.error('Error saving alert:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Název alertu</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Slack Channel ID</label>
        <input
          type="text"
          value={formData.slackChannelId}
          onChange={(e) => setFormData({ ...formData, slackChannelId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="C0123456789"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Typ nemovitosti</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="byt">Byt</option>
            <option value="dum">Dům</option>
            <option value="pozemek">Pozemek</option>
            <option value="chata">Chata</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Typ nabídky</label>
          <select
            value={formData.offerType}
            onChange={(e) => setFormData({ ...formData, offerType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="prodej">Prodej</option>
            <option value="pronajem">Pronájem</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
        <select
          value={formData.region}
          onChange={(e) => setFormData({ ...formData, region: e.target.value, districts: [] })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {regionOptions.map((region) => (
            <option key={region.value} value={region.value}>
              {region.label}
            </option>
          ))}
        </select>
      </div>

      {/* Districts Section */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Městské části {formData.districts.length > 0 && <span className="text-blue-600">({formData.districts.length} vybráno)</span>}
        </label>
        {loadingDistricts ? (
          <div className="text-sm text-gray-500 py-3">Načítání městských částí...</div>
        ) : availableDistricts.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {availableDistricts.map((districtObj) => (
              <button
                key={districtObj.district}
                type="button"
                onClick={() => handleDistrictChange(districtObj.district)}
                className={`px-3 py-2 border rounded-lg text-left text-sm transition-colors ${
                  formData.districts.some((d) => d.toLowerCase() === districtObj.district.toLowerCase())
                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                    : 'border-gray-300 hover:border-gray-400 bg-white'
                }`}
              >
                {districtObj.district} ({districtObj.count})
              </button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 py-3">Žádné městské části k dispozici</div>
        )}
      </div>

      {/* Price section */}
      <div className="space-y-4">
        {/* Min Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Minimální cena za m²</label>
          <div className="flex gap-2">
            <select
              value={formData.conditions.priceMinType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  conditions: {
                    ...formData.conditions,
                    priceMinType: e.target.value,
                    priceMin: e.target.value === 'special' ? 'median' : '',
                  },
                })
              }
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="number">Číslo</option>
              <option value="special">Speciální</option>
            </select>

            {formData.conditions.priceMinType === 'number' ? (
              <input
                type="number"
                value={formData.conditions.priceMin}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditions: {
                      ...formData.conditions,
                      priceMin: e.target.value ? parseInt(e.target.value) : '',
                    },
                  })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Kč/m²"
              />
            ) : (
              <select
                value={formData.conditions.priceMin}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditions: {
                      ...formData.conditions,
                      priceMin: e.target.value,
                    },
                  })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="median">Medián v lokalitě</option>
                <option value="median_minus_10">10% pod mediánem</option>
                <option value="median_minus_20">20% pod mediánem</option>
              </select>
            )}
          </div>
        </div>

        {/* Max Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Maximální cena za m²</label>
          <div className="flex gap-2">
            <select
              value={formData.conditions.priceMaxType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  conditions: {
                    ...formData.conditions,
                    priceMaxType: e.target.value,
                    priceMax: e.target.value === 'special' ? 'median' : '',
                  },
                })
              }
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="number">Číslo</option>
              <option value="special">Speciální</option>
            </select>

            {formData.conditions.priceMaxType === 'number' ? (
              <input
                type="number"
                value={formData.conditions.priceMax}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditions: {
                      ...formData.conditions,
                      priceMax: e.target.value ? parseInt(e.target.value) : '',
                    },
                  })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Kč/m²"
              />
            ) : (
              <select
                value={formData.conditions.priceMax}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditions: {
                      ...formData.conditions,
                      priceMax: e.target.value,
                    },
                  })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="median">Medián v lokalitě</option>
                <option value="median_plus_10">10% nad mediánem</option>
                <option value="median_plus_20">20% nad mediánem</option>
              </select>
            )}
          </div>
        </div>

        {/* Additional Options */}
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.linkToJestrab}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  linkToJestrab: e.target.checked,
                })
              }
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Odkaz na Jestřáb</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.valuationEnabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  valuationEnabled: e.target.checked,
                })
              }
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Povolit ocenění</span>
          </label>
        </div>
      </div>

      {/* Layouts Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Dispozice</label>
        <div className="grid grid-cols-3 gap-2">
          {['1+kk', '1+1', '2+kk', '2+1', '3+kk', '3+1', '4+kk', '4+1', '5+kk', '5+1', '6+kk', '6+1'].map((layout) => (
            <label key={layout} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.layouts.includes(layout)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      layouts: [...formData.layouts, layout],
                    })
                  } else {
                    setFormData({
                      ...formData,
                      layouts: formData.layouts.filter((l) => l !== layout),
                    })
                  }
                }}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{layout}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Stavba Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Stavba</label>
        <div className="space-y-2">
          {filterConditions.map(({ filterKey, label }) => (
            <label key={filterKey} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.conditions.stavba.includes(filterKey)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      conditions: {
                        ...formData.conditions,
                        stavba: [...formData.conditions.stavba, filterKey],
                      },
                    })
                  } else {
                    setFormData({
                      ...formData,
                      conditions: {
                        ...formData.conditions,
                        stavba: formData.conditions.stavba.filter((s) => s !== filterKey),
                      },
                    })
                  }
                }}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {/* <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.conditions.activeOnly}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, activeOnly: e.target.checked },
                  })
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Pouze aktivní inzeráty</span>
            </label> */}
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSaving}
          className={`flex-1 px-4 py-2 text-white rounded-md ${isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {isSaving ? 'Ukládám...' : alert ? 'Uložit změny' : 'Vytvořit alert'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className={`flex-1 px-4 py-2 border border-gray-300 rounded-md ${isSaving ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          Zrušit
        </button>
      </div>
    </form>
  )
}

export default NotificationsModal
